import { CSSProperties, useMemo } from "react";

/**
 * Custom Rings loader component that replicates the react-loader-spinner Rings component.
 * Creates SVG-based rings that pulse outward with proper timing and radius control.
 *
 * @component
 * @example
 * <RingsLoader
 *   visible={true}
 *   height={80}
 *   width={80}
 *   color="#4fa94d"
 *   radius={6}
 *   ariaLabel="rings-loading"
 * />
 */
interface RingsLoaderProps {
  /** Whether the loader is visible */
  visible?: boolean;
  /** Height of the loader in pixels */
  height?: number | string;
  /** Width of the loader in pixels */
  width?: number | string;
  /** Color of the rings */
  color?: string;
  /** Radius of the ring stroke */
  radius?: number | string;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Additional CSS classes for wrapper */
  wrapperClass?: string;
  /** Inline styles for wrapper */
  wrapperStyle?: CSSProperties;
}

const RingsLoader = ({
  visible = true,
  height = 80,
  width = 80,
  color = "#4fa94d",
  radius = 3,
  ariaLabel = "rings-loading",
  wrapperClass = "",
  wrapperStyle = {},
}: RingsLoaderProps) => {
  if (!visible) return null;

  // Convert to numbers if strings
  const heightNum = typeof height === "string" ? parseInt(height) : height;
  const widthNum = typeof width === "string" ? parseInt(width) : width;
  const radiusNum = typeof radius === "string" ? parseInt(radius) : radius;

  // Calculate SVG dimensions and circle properties
  const size = Math.min(heightNum, widthNum);
  const center = size / 2;
  const strokeWidth = radiusNum;

  // Create stable animation name per component instance
  const animationName = useMemo(
    () => `rings-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  return (
    <>
      <style>
        {`
          @keyframes ${animationName}-inner {
            0% {
              r: ${center * 0.1};
              opacity: 1;
            }
            70% {
              r: ${center * 0.4};
              opacity: 1;
            }
            100% {
              r: ${center * 0.1};
              opacity: 0.7;
            }
          }

          @keyframes ${animationName}-outer {
            0% {
              r: 0;
              opacity: 1;
            }
            100% {
              r: ${center - strokeWidth};
              opacity: 0;
            }
          }
        `}
      </style>
      <div
        className={wrapperClass}
        style={{
          width: `${widthNum}px`,
          height: `${heightNum}px`,
          display: "inline-block",
          ...wrapperStyle,
        }}
        role="status"
        aria-label={ariaLabel}
      >
        <svg
          width={widthNum}
          height={heightNum}
          viewBox={`0 0 ${size} ${size}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Inner Ring - pulses in and out */}
          <circle
            cx={center}
            cy={center}
            r="0"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            style={{
              animationName: `${animationName}-inner`,
              animationDuration: "2.5s",
              animationTimingFunction: "ease-in-out",
              animationIterationCount: "infinite",
              animationDelay: "0s",
            }}
          />

          {/* Outer Ring - grows outward and fades */}
          <circle
            cx={center}
            cy={center}
            r="0"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            style={{
              animationName: `${animationName}-outer`,
              animationDuration: "2.5s",
              animationTimingFunction: "ease-out",
              animationIterationCount: "infinite",
              animationDelay: "2.5s",
            }}
          />
        </svg>

        {/* Screen reader text */}
        <span className="sr-only">Loading...</span>
      </div>
    </>
  );
};

export default RingsLoader;
